import type { NoteColor } from '@/enums/noteColor';
import type { Note, NoteId } from '@/types/note';
import { newNoteId } from '@/utils/id';
import { nextZIndex } from '@/utils/zOrder';
import { createNoteInputSchema, noteSchema } from '@/validation/noteSchema';
import { PersistentStore } from '@/services/cache/PersistentStore';
import {
  ConflictError,
  NotFoundError,
  ValidationApiError,
} from './errors';
import { simulateLatency } from './latency';

export interface CreateNoteInput {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly color: NoteColor;
  readonly text: string;
}

export type UpdatePatch = Partial<
  Pick<Note, 'x' | 'y' | 'width' | 'height' | 'color' | 'text' | 'zIndex'>
>;

export interface NotesApi {
  list(signal?: AbortSignal): Promise<Note[]>;
  get(id: NoteId, signal?: AbortSignal): Promise<Note>;
  create(input: CreateNoteInput, signal?: AbortSignal): Promise<Note>;
  update(id: NoteId, patch: UpdatePatch, expectedVersion: number, signal?: AbortSignal): Promise<Note>;
  delete(id: NoteId, expectedVersion: number, signal?: AbortSignal): Promise<void>;
  batchUpsert(notes: readonly Note[], signal?: AbortSignal): Promise<Note[]>;
  reset(): void;
}

/**
 * In-memory mock persistence for the API layer. Kept isolated from the client
 * cache so we can simulate an authoritative server (version increments,
 * conflict detection) without depending on the frontend cache state.
 *
 * Seeded from localStorage (via PersistentStore) on construction so mock and
 * persistent tier stay coherent on reload.
 */
class MockNotesApi implements NotesApi {
  private readonly store = new Map<NoteId, Note>();

  constructor(private readonly persistent: PersistentStore = new PersistentStore()) {
    const outcome = persistent.load();
    for (const n of outcome.notes) this.store.set(n.id, n);
  }

  async list(signal?: AbortSignal): Promise<Note[]> {
    await simulateLatency(signal);
    return [...this.store.values()].sort((a, b) => a.zIndex - b.zIndex);
  }

  async get(id: NoteId, signal?: AbortSignal): Promise<Note> {
    await simulateLatency(signal);
    const note = this.store.get(id);
    if (!note) throw new NotFoundError(id);
    return note;
  }

  async create(input: CreateNoteInput, signal?: AbortSignal): Promise<Note> {
    await simulateLatency(signal);
    const validated = createNoteInputSchema(input);
    if (!validated.ok) {
      throw new ValidationApiError(
        validated.error.map((e) => ({ path: e.path, message: e.message })),
      );
    }
    const now = Date.now();
    const note: Note = {
      id: newNoteId(),
      x: input.x,
      y: input.y,
      width: input.width,
      height: input.height,
      color: input.color,
      text: input.text,
      zIndex: nextZIndex([...this.store.values()]),
      version: 1,
      createdAt: now,
      updatedAt: now,
    };
    this.store.set(note.id, note);
    this.persist();
    return note;
  }

  async update(
    id: NoteId,
    patch: UpdatePatch,
    expectedVersion: number,
    signal?: AbortSignal,
  ): Promise<Note> {
    await simulateLatency(signal);
    const existing = this.store.get(id);
    if (!existing) throw new NotFoundError(id);
    if (existing.version !== expectedVersion) {
      throw new ConflictError(id, expectedVersion, existing.version);
    }
    const merged: Note = {
      ...existing,
      ...patch,
      version: existing.version + 1,
      updatedAt: Date.now(),
    };
    const validated = noteSchema(merged);
    if (!validated.ok) {
      throw new ValidationApiError(
        validated.error.map((e) => ({ path: e.path, message: e.message })),
      );
    }
    this.store.set(id, validated.value);
    this.persist();
    return validated.value;
  }

  async delete(id: NoteId, expectedVersion: number, signal?: AbortSignal): Promise<void> {
    await simulateLatency(signal);
    const existing = this.store.get(id);
    if (!existing) throw new NotFoundError(id);
    if (existing.version !== expectedVersion) {
      throw new ConflictError(id, expectedVersion, existing.version);
    }
    this.store.delete(id);
    this.persist();
  }

  async batchUpsert(notes: readonly Note[], signal?: AbortSignal): Promise<Note[]> {
    await simulateLatency(signal);
    const out: Note[] = [];
    for (const n of notes) {
      const existing = this.store.get(n.id);
      const next: Note = existing
        ? { ...n, version: existing.version + 1, updatedAt: Date.now() }
        : { ...n, version: 1, updatedAt: Date.now(), createdAt: Date.now() };
      this.store.set(next.id, next);
      out.push(next);
    }
    this.persist();
    return out;
  }

  reset(): void {
    this.store.clear();
    this.persist();
  }

  private persist(): void {
    this.persistent.save([...this.store.values()]);
  }
}

let singleton: NotesApi | null = null;

export function getNotesApi(): NotesApi {
  if (!singleton) singleton = new MockNotesApi();
  return singleton;
}

/** Test seam: swap out the singleton. */
export function setNotesApi(api: NotesApi): void {
  singleton = api;
}

export function createMockNotesApi(persistent?: PersistentStore): NotesApi {
  return persistent ? new MockNotesApi(persistent) : new MockNotesApi();
}
